// sections/SectionAction.jsx
import React, { useState, useCallback } from 'react';
import { CheckCircle, ArrowRightCircle, RefreshCcw, Lock } from 'lucide-react';
import { Row, Col, Button, SectionTitle } from '../../../SharedInputs.jsx';
import {
  acceptEnrollment,
  shiftEnrollmentToAccelerate,
  lockEnrolmentData,
  updateEnrollmentToAccelerate,
  updateEnrollmentApplicationForm
} from '../../../../services/EnrolmentService.js';
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { renderAsync } from "docx-preview";
import { CODE_TO_COUNTRY } from '../../../../CountrySACC.js';
import { fileStorageService } from "../../../../services/FileStorageService.js";
import { convertPDF } from "../../../../services/EnrolmentService.js"
import EnrolmentMapping from '../../../TemplateMapping/EnrolmentMapping.js';
import { handleDocxForm } from '../../FormUtils.js';
import ImageModule from "docxtemplater-image-module-free";
import {createStateStudent, updateStudent, getAllStudents} from "../../../../services/StudentRecordService.js";

export function SectionAction({ initialData: initialData, onCancel, setParentInitialData }) {
  const [processedContent, setProcessedContent] = useState(null);
  const [error, setError] = useState('');
  const getCountryCode = (countryCode) => CODE_TO_COUNTRY[countryCode] || '';
  const accepted = initialData?.status === "accepted";
  const pending = initialData?.status === "pending";
  const shifted = initialData?.migratedToStudentSystem || false;
  // Correctly declare state
  const response = initialData?.axcelerateResponse || { type: "", message: "" };
  const templatePath = initialData?.category === 'domestic' ? "/templates/Application for Enrolment Form - Domestic Students V5.0  April 2025.docx" : "/templates/Application for Enrolment Form - International Students V5.0  April 2025.docx";
  const mapStudent = (initialData) => ({
    studentId: "",
    firstName: initialData.formState.personal.givenNames || "",
    lastName: initialData.formState.personal.familyName || "",
    gender: initialData.formState.personal.gender || "",
    dateOfBirth: initialData.formState.personal.dateOfBirth || "",
    email: initialData.formState.personal.email,
    phone: initialData.formState.personal.mobilePhone,
    createdBy: "",
    onshoreAddress: initialData.formState.personal.address + " " + initialData.formState.personal.suburb + " " + getCountryCode(initialData.formState.personal.country)+ " " + initialData.formState.personal.postcode || "",
    offshoreAddress: initialData.formState.personal.permanentHomeAddress || "",
    campusName: "",
    coeType: "",
    visaApplicationType: "",
    agentName: initialData.formState.personal.agencyName,
    usiNumber: initialData.formState.personal.usiNumber,
    studentStatus: "New Enrolment",
    leadingTo: "",
  });

  const handleAcceptEnrolment = async () => {
    if (!initialData?.id || accepted) return;

    try {
      const mappedStudentData = mapStudent(initialData);
      let response = (initialData.previouslyStudiedAtCIET == "YES" && initialData.studentId !== "") ? await getStudentRecord(initialData.studentId) : "";
      
      if (response !== "") {
        await updateStudent(response, initialData.id, mappedStudentData);
      } else {
        // Create new student
        const result = await createStateStudent(mappedStudentData, initialData.id, "");
        response = result; 
      }
      await acceptEnrollment(initialData.id, response);
      setParentInitialData({ ...initialData, status: "accepted", enrolmentAcceptedAt: new Date().toISOString(), studentId: response });
    } catch (err) {
      console.log("Error", err);
    }
  };
  const getStudentRecord = async (studentId) => {
    try {
      const students = await getAllStudents(studentId);
      return students?.[0]?.id || "";
    } catch (error) {
      console.error("Error fetching student:", error);
      return "";
    }
  };

  const handleShiftToAxcelerate = async () => {
    if (!accepted || !initialData?.id || shifted) return;

    try {
      const dbUpdate = await shiftEnrollmentToAccelerate(initialData.id, initialData);
      // Update response state for UI
      let message = "";
      if (dbUpdate.type === 'success') {
        message = "Enrollment shifted successfully: " + (dbUpdate.message || 'Completed');
        setParentInitialData({ ...initialData, migratedToStudentSystem: true, axcelerateResponse: { type: dbUpdate.type, message: message }, axcelerateContactID: dbUpdate.axcelerateContactID });
        const updateEnrolmentDB = await updateEnrollmentToAccelerate(initialData.id, dbUpdate.axcelerateContactID, initialData);
        // Update response state for UI
        let updateMessage = "";
        if (updateEnrolmentDB.type === 'success') {
          updateMessage = "Enrollment shifted successfully: " + (updateEnrolmentDB.message || 'Completed');
          setParentInitialData({ ...initialData, migratedToStudentSystem: true, axcelerateResponse: { type: updateEnrolmentDB.type, message: updateMessage }, updatedAxcelerateAt: new Date().toISOString() });
        } else {
          updateMessage = "" + (updateEnrolmentDB.message || "Check details");
          setParentInitialData({ ...initialData, axcelerateResponse: { type: updateEnrolmentDB.type, message: updateMessage }, updatedAxcelerateAt: new Date().toISOString() });
        }
      } else {
        message = "" + (dbUpdate.message || "Check details");
        setParentInitialData({ ...initialData, axcelerateResponse: { type: dbUpdate.type, message }, updatedAxcelerateAt: new Date().toISOString() });
      }

    } catch (err) {
      const errorMessage = "Failed to shift enrollment. " + (err.details || err.message);
      setParentInitialData({ ...initialData, axcelerateResponse: { type: "error", message: errorMessage }, updatedAxcelerateAt: new Date().toISOString() });
    }
  };
  const processDocxTemplate = (arrayBuffer, mapping) => {
    const zip = new PizZip(arrayBuffer);
    // Image module configuration
    const imageOpts = {
      centered: false, // Set to true if you want centered images
      getImage: (tagValue) => {
        // Convert base64 to array buffer
        if (tagValue && tagValue.startsWith('data:image')) {
          // Remove data:image/png;base64, prefix
          const base64Data = tagValue.split(',')[1];
          const binaryString = atob(base64Data);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return bytes;
        }
        // If just base64 without prefix
        const binaryString = atob(tagValue);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      },
      getSize: (img, tagValue, tagName) => {
        return [250, 100]; // Default size
      }
    };
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: "[[", end: "]]" },  // <-- must match template
      modules: [new ImageModule(imageOpts)]
    });

    try {
      doc.render(mapping);
    } catch (error) {
      throw error;
    }

    const out = doc.getZip().generate({ type: "blob" });
    return out;
  };

  // Mapping function to convert your data structure to template placeholders
  const createTemplateMapping = (data) => {
    const getCountryCode = (countryCode) => CODE_TO_COUNTRY[countryCode] || '';
    return {
      // Personal Details
      'P_Y': data.personal?.previouslyStudiedAtCIET === 'Yes' ? '☑' : '☐',
      'P_N': data.personal?.previouslyStudiedAtCIET === 'No' ? '☑' : '☐',
      'STUDENT_ID': data.personal?.studentId || '',
      'USI_NUMBER': data.personal?.usiNumber || data.usi?.usi || '',
      'TITLE': data.personal?.title || '',
      'GIVEN_NAMES': data.personal?.givenNames || '',
      'FAMILY_NAME': data.personal?.familyName || '',
      'D_O_B': data.personal?.dateOfBirth || '',
      'GENDER': data.personal?.gender || '',
      'COUNTRY_OF_BIRTH': getCountryCode(data.personal?.countryOfBirth) || '',
      'CITIZENSHIP': getCountryCode(data.personal?.citizenship) || '',
      'PASSPORT_NUMBER': data.personal?.passportNumber || '',
      'PASSPORT_EXPIRY': data.personal?.passportExpiry || '',
      'COUNTRY_OF_ISSUE': getCountryCode(data.personal?.passportCountryOfIssue) || '',
      'AU_Y': data.personal?.inAustraliaAtApplication === 'Yes' ? '☑' : '☐',
      'AU_N': data.personal?.inAustraliaAtApplication === 'No' ? '☑' : '☐',
      'Y_O_ARRIVAL': data.personal?.yearOfArrival,
      'C_E_STATUS': data.employmentStudyReason?.employmentStatus || '',
      'MARTIAL_STATUS': data.personal.maritalStatus || '',
      'MOBILE_PHONE': data.personal?.mobilePhone || '',
      'TELEPHONE': data.personal.telephone || '',
      'EMERGENCY_NAME': data.personal?.emergencyContactName || '',
      'RELATION': data.personal.emergencyContactRelation || '',
      'R_CONTACT': data.personal?.emergencyContactNumber || '',
      'EMAIL': data.personal?.email || '',
      'ADDRESS': data.personal?.address || '',
      'SUBURB': data.personal?.suburb || '',
      'STATE': data.personal?.state === 'OTHER'
              ? data.personal?.otherState || ''
              : data.personal?.state || '',
      'POSTCODE': data.personal?.postcode || '',
      'COUNTRY': getCountryCode(data.personal?.country) || '',
      'PERMANENT_ADD': data.personal.permanentHomeAddress || '',
      'AGENCY_NAME': data.personal.agencyName || '',
      
      // Campus
      'C_CAN': data.campusCourse?.campus === 'Canberra Campus' ? '☑' : '☐',
      'C_MEL': data.campusCourse?.campus === 'Melbourne Campus' ? '☑' : '☐',
      
      // Courses - you'll need to map each course
      'C_30821': data.campusCourse?.courses?.some(c => c.startsWith('SIT30821')) ? '☑' : '☐',
      'C_50420': data.campusCourse?.courses?.some(c => c.startsWith('BSB50420')) ? '☑' : '☐',
      'C_40521': data.campusCourse?.courses?.some(c => c.startsWith('SIT40521')) ? '☑' : '☐',
      'C_60420': data.campusCourse?.courses?.some(c => c.startsWith('BSB60420')) ? '☑' : '☐',
      'C_50422': data.campusCourse?.courses?.some(c => c.startsWith('SIT50422')) ? '☑' : '☐',
      'C_80120': data.campusCourse?.courses?.some(c => c.startsWith('BSB80120')) ? '☑' : '☐',
      'C_60322': data.campusCourse?.courses?.some(c => c.startsWith('SIT60322')) ? '☑' : '☐',
      'C_30220': data.campusCourse?.courses?.some(c => c.startsWith('CPC30220')) ? '☑' : '☐',
      'C_50220_IT': data.campusCourse?.courses?.some(c => c.startsWith('ICT50220')) ? '☑' : '☐',
      'C_33020': data.campusCourse?.courses?.some(c => c.startsWith('CPC33020')) ? '☑' : '☐',
      'C_60220_IT': data.campusCourse?.courses?.some(c => c.startsWith('ICT60220')) ? '☑' : '☐',
      'C_30620': data.campusCourse?.courses?.some(c => c.startsWith('CPC30620')) ? '☑' : '☐',
      'C_50220_Building': data.campusCourse?.courses?.some(c => c.startsWith('CPC50220')) ? '☑' : '☐',
      'C_40120': data.campusCourse?.courses?.some(c => c.startsWith('CPC40120')) ? '☑' : '☐',
      'C_33021': data.campusCourse?.courses?.some(c => c.startsWith('CHC33021')) ? '☑' : '☐',
      'C_60220_Building': data.campusCourse?.courses?.some(c => c.startsWith('CPC60220')) ? '☑' : '☐',
      'C_43121': data.campusCourse?.courses?.some(c => c.startsWith('CHC43121')) ? '☑' : '☐',
      'C_52021': data.campusCourse?.courses?.some(c => c.startsWith('CHC52021')) ? '☑' : '☐',
      'C_WHS1001': data.campusCourse?.courses?.some(c => c.startsWith('CPCCWHS1001')) ? '☑' : '☐',
      'C_11084NAT': data.campusCourse?.courses?.some(c => c.startsWith('11084NAT')) ? '☑' : '☐',
      'C_10830NAT': data.campusCourse?.courses?.some(c => c.startsWith('10830NAT')) ? '☑' : '☐',
      'C_AID003': data.campusCourse?.courses?.some(c => c.startsWith('HLTAID003')) ? '☑' : '☐',


      // Intake
      'I_J': data.intake?.month?.toLowerCase() === 'jan' ? '☑' : '☐',
      'I_F': data.intake?.month?.toLowerCase() === 'feb' ? '☑' : '☐',
      'I_M': data.intake?.month?.toLowerCase() === 'mar' ? '☑' : '☐',
      'I_A': data.intake?.month?.toLowerCase() === 'apr' ? '☑' : '☐',
      'I_MA': data.intake?.month?.toLowerCase() === 'may' ? '☑' : '☐',
      'I_JU': data.intake?.month?.toLowerCase() === 'jun' ? '☑' : '☐',
      'I_JL': data.intake?.month?.toLowerCase() === 'jul' ? '☑' : '☐',
      'I_AU': data.intake?.month?.toLowerCase() === 'aug' ? '☑' : '☐',
      'I_S': data.intake?.month?.toLowerCase() === 'sep' ? '☑' : '☐',
      'I_O': data.intake?.month?.toLowerCase() === 'oct' ? '☑' : '☐',
      'I_N': data.intake?.month?.toLowerCase() === 'nov' ? '☑' : '☐',
      'I_D': data.intake?.month?.toLowerCase() === 'dec' ? '☑' : '☐',
      'I_YEAR': data.intake?.year || '',

      // Visa
      'VISA_OFF': data.visa?.visaStatus === 'No Visa Yet (Offshore Application)' ? '☑' : '☐',
      'VISA_CUR': data.visa?.visaStatus.includes('Current') ? '☑' : '☐',
      'VISA_PRE': data.visa?.visaStatus.includes('Previous') ? '☑' : '☐',
      'VISA_STU': data.visa?.visaStatus === 'Current Student Visa' ? '☑' : '☐',
      'VISA_TOU': data.visa?.visaStatus === 'Current Tourist Visa' ? '☑' : '☐',
      'VISA_OTH': data.visa?.visaStatus === 'Current Other Visa' ? '☑' : '☐',
      'VISA_TYPE': data.visa?.visaStatus + " " + data.visa?.visaType || '',
      'V_AP_Y': data.visa?.appliedBefore === 'Yes' ? '☑' : '☐',
      'V_AP_N': data.visa?.appliedBefore === 'No' ? '☑' : '☐',
      'EMBASSY_OFFICE': data.visa?.embassyOffice || '',
      'V_G_Y': data.visa?.granted === 'Yes' ? '☑' : '☐',
      'V_G_N': data.visa?.granted === 'No' ? '☑' : '☐',
      'V_REF_DATE': data.visa?.refusalDate || '',
      'V_REF_LET': data.visa?.refusalLetter || '',
      'V_REF_REASON': data.visa?.refusalReason || '',
      
      // Language & Culture
      'B_AU': getCountryCode(data.languageCulture?.country) === 'Australia' ? '☑' : '☐',
      'B_OT': getCountryCode(data.languageCulture?.country) !== 'Australia' ? '☑' : '☐',
      'B_OT_SPEC': getCountryCode(data.languageCulture?.country) !== 'Australia' ? getCountryCode(data.languageCulture?.country) : '',
      'LANG_EN': data.languageCulture?.languageAtHome === 'English only' ? '☑' : '☐',
      'LANG_OT': data.languageCulture?.languageAtHome !== 'English only' ? '☑' : '☐',
      'LANG_SPEC': data.languageCulture?.otherLanguage || '',
      'T_IELTS': 'IELTS Academic' === data.languageCulture?.englishTest ? '☑' : '☐',
      'T_TOEFL': data.languageCulture?.englishTest === 'TOEFL iBT' ? '☑' : '☐',
      'T_PTE': data.languageCulture?.englishTest === 'PTE Academic' ? '☑' : '☐',
      'T_OTHER': !['IELTS Academic','TOEFL iBT','PTE Academic',''].includes(data.languageCulture?.englishTest) ? '☑' : '☐',
      'T_OTHER_SPEC': !['IELTS Academic','TOEFL iBT','PTE Academic',''].includes(data.languageCulture?.englishTest) ? data.languageCulture?.englishTest : '',
      'ENG_SCORE': data.languageCulture?.overallScore || '',
      'T_DATE': data.languageCulture?.testDate || '',
      'AB_NO': data.languageCulture?.aboriginalOrTorres?.includes('No') ? '☑' : '☐',
      'AB_YES': data.languageCulture?.aboriginalOrTorres?.includes('Aboriginal') ? '☑' : '☐',
      'TORRES_YES': data.languageCulture?.aboriginalOrTorres?.includes('Torres Strait Islander') ? '☑' : '☐',

      // Disability
      'DISABILITY_YES': data.disability?.hasDisability === 'Yes' ? '☑' : '☐',
      'DISABILITY_NO': data.disability?.hasDisability === 'No' ? '☑' : '☐',

      // Disability Areas
      'D_HEAR': data.disability?.areas?.includes('Hearing/deaf') ? '☑' : '☐',
      'D_PHYS': data.disability?.areas?.includes('Physical') ? '☑' : '☐',
      'D_INTEL': data.disability?.areas?.includes('Intellectual') ? '☑' : '☐',
      'D_LEARN': data.disability?.areas?.includes('Learning') ? '☑' : '☐',
      'D_MENT': data.disability?.areas?.includes('Mental illness') ? '☑' : '☐',
      'D_ABI': data.disability?.areas?.includes('Acquired brain impairment') ? '☑' : '☐',
      'D_VISION': data.disability?.areas?.includes('Vision') ? '☑' : '☐',
      'D_MED': data.disability?.areas?.includes('Medical condition') ? '☑' : '☐',
      'D_OTHER': data.disability?.areas?.includes('Other') ? '☑' : '☐',

      // Highest Completed School Level
      'S_12': data.schoolingQualifications?.highestSchoolLevel === 'Year 12 or equivalent' ? '☑' : '☐',
      'S_11': data.schoolingQualifications?.highestSchoolLevel === 'Year 11 or equivalent' ? '☑' : '☐',
      'S_10': data.schoolingQualifications?.highestSchoolLevel === 'Year 10 or equivalent' ? '☑' : '☐',
      'S_9': data.schoolingQualifications?.highestSchoolLevel === 'Year 9 or equivalent' ? '☑' : '☐',
      'S_8': data.schoolingQualifications?.highestSchoolLevel === 'Year 8 or below' ? '☑' : '☐',
      'S_NONE': data.schoolingQualifications?.highestSchoolLevel === 'Never attended school' ? '☑' : '☐',

      // Still Enrolled in Secondary
      'SEC_YES': data.schoolingQualifications?.stillEnrolledSecondary === 'Yes' ? '☑' : '☐',
      'SEC_NO': data.schoolingQualifications?.stillEnrolledSecondary === 'No' ? '☑' : '☐',

      // Currently Enrolled in Any Studies
      'CURR_YES': data.schoolingQualifications?.currentlyEnrolledAnyStudies === 'Yes' ? '☑' : '☐',
      'CURR_NO': data.schoolingQualifications?.currentlyEnrolledAnyStudies === 'No' ? '☑' : '☐',

      // Current Study Details
      'CURR_NAME': data.schoolingQualifications?.currentProviderName || '',
      'CURR_COURSE': data.schoolingQualifications?.currentProviderCourse || '',
      'CURR_CONTACT': data.schoolingQualifications?.currentProviderContact || '',

      // Completed English Qualifications
      'ENG_QUAL_YES': data.schoolingQualifications?.completedQualificationsInEnglish === 'Yes' ? '☑' : '☐',
      'ENG_QUAL_NO': data.schoolingQualifications?.completedQualificationsInEnglish === 'No' ? '☑' : '☐',

      // Completed Levels in English (array of strings)
      'ENG_LEVELS': data.schoolingQualifications?.completedLevels || [],
      // Specific Completed Levels (array of strings)
      'ENG_BACH': data.schoolingQualifications?.completedLevels?.includes('higher degree') ? '☑' : '☐',
      'ENG_ADV_DIP': data.schoolingQualifications?.completedLevels?.includes('Advanced diploma or associate degree') ? '☑' : '☐',
      'ENG_DIP': data.schoolingQualifications?.completedLevels?.includes('Diploma (or associate diploma)') ? '☑' : '☐',
      'ENG_CERT4': data.schoolingQualifications?.completedLevels?.includes('Certificate IV (or advanced certificate/technician)') ? '☑' : '☐',
      'ENG_CERT3': data.schoolingQualifications?.completedLevels?.includes('Certificate III (or trade certificate)') ? '☑' : '☐',
      'ENG_CERT2': data.schoolingQualifications?.completedLevels?.includes('Certificate II') ? '☑' : '☐',
      'ENG_CERT1': data.schoolingQualifications?.completedLevels?.includes('Certificate I') ? '☑' : '☐',
      'ENG_OTHER': data.schoolingQualifications?.completedLevels?.includes('Other education') ? '☑' : '☐',

      // Prior schoolingQualifications List (example for first 3 institutions; you can loop dynamically)
      'PRI_1_NAME': data.schoolingQualifications?.priorEducationList?.[0]?.institution || '',
      'PRI_1_QUAL': data.schoolingQualifications?.priorEducationList?.[0]?.qualification || '',
      'PRI_1_LOC': data.schoolingQualifications?.priorEducationList?.[0]?.location || '',
      'PRI_1_YEAR': data.schoolingQualifications?.priorEducationList?.[0]?.yearCompleted || '',
      'PRI_1_STATUS': data.schoolingQualifications?.priorEducationList?.[0]?.status || 'Completed',

      'PRI_2_NAME': data.schoolingQualifications?.priorEducationList?.[1]?.institution || '',
      'PRI_2_QUAL': data.schoolingQualifications?.priorEducationList?.[1]?.qualification || '',
      'PRI_2_LOC': data.schoolingQualifications?.priorEducationList?.[1]?.location || '',
      'PRI_2_YEAR': data.schoolingQualifications?.priorEducationList?.[1]?.yearCompleted || '',
      'PRI_2_STATUS': data.schoolingQualifications?.priorEducationList?.[1]?.status || 'Completed',

      'PRI_3_NAME': data.schoolingQualifications?.priorEducationList?.[2]?.institution || '',
      'PRI_3_QUAL': data.schoolingQualifications?.priorEducationList?.[2]?.qualification || '',
      'PRI_3_LOC': data.schoolingQualifications?.priorEducationList?.[2]?.location || '',
      'PRI_3_YEAR': data.schoolingQualifications?.priorEducationList?.[2]?.yearCompleted || '',
      'PRI_3_STATUS': data.schoolingQualifications?.priorEducationList?.[2]?.status || 'Completed',

      'PRI_4_NAME': data.schoolingQualifications?.priorEducationList?.[3]?.institution || '',
      'PRI_4_QUAL': data.schoolingQualifications?.priorEducationList?.[3]?.qualification || '',
      'PRI_4_LOC': data.schoolingQualifications?.priorEducationList?.[3]?.location || '',
      'PRI_4_YEAR': data.schoolingQualifications?.priorEducationList?.[3]?.yearCompleted || '',

      'PRI_5_NAME': data.schoolingQualifications?.priorEducationList?.[4]?.institution || '',
      'PRI_5_QUAL': data.schoolingQualifications?.priorEducationList?.[4]?.qualification || '',
      'PRI_5_LOC': data.schoolingQualifications?.priorEducationList?.[4]?.location || '',
      'PRI_5_YEAR': data.schoolingQualifications?.priorEducationList?.[4]?.yearCompleted || '',

      'PRI_6_NAME': data.schoolingQualifications?.priorEducationList?.[5]?.institution || '',
      'PRI_6_QUAL': data.schoolingQualifications?.priorEducationList?.[5]?.qualification || '',
      'PRI_6_LOC': data.schoolingQualifications?.priorEducationList?.[5]?.location || '',
      'PRI_6_YEAR': data.schoolingQualifications?.priorEducationList?.[5]?.yearCompleted || '',

      'PRI_7_NAME': data.schoolingQualifications?.priorEducationList?.[6]?.institution || '',
      'PRI_7_QUAL': data.schoolingQualifications?.priorEducationList?.[6]?.qualification || '',
      'PRI_7_LOC': data.schoolingQualifications?.priorEducationList?.[6]?.location || '',
      'PRI_7_YEAR': data.schoolingQualifications?.priorEducationList?.[6]?.yearCompleted || '',
      
      // OSHC
      'OSHC_YES': data.oshc?.arrangeOSHC === 'Yes' ? '☑' : '☐',
      'OSHC_NO': data.oshc?.arrangeOSHC === 'No' ? '☑' : '☐',
      'OSHC_SINGLE': data.oshc?.coverType === 'Single' ? '☑' : '☐',
      'OSHC_COUPLE': data.oshc?.coverType === 'Couple' ? '☑' : '☐',
      'OSHC_FAMILY': data.oshc?.coverType === 'Family' ? '☑' : '☐',

      // Employment Status (contains check)
      'EMP_FULL': data.employmentStudyReason?.employmentStatus?.includes('Full-time employee') ? '☑' : '☐',
      'EMP_PART': data.employmentStudyReason?.employmentStatus?.includes('Part-time employee') ? '☑' : '☐',
      'EMP_SELF_NO': data.employmentStudyReason?.employmentStatus?.includes('not employing others') ? '☑' : '☐',
      'EMP_SELF_YES': data.employmentStudyReason?.employmentStatus?.includes('employing others') ? '☑' : '☐',
      'EMP_UNPAID': data.employmentStudyReason?.employmentStatus?.includes('unpaid worker') ? '☑' : '☐',
      'EMP_UNEMP_FULL': data.employmentStudyReason?.employmentStatus?.includes('seeking full-time work') ? '☑' : '☐',
      'EMP_UNEMP_PART': data.employmentStudyReason?.employmentStatus?.includes('seeking part-time work') ? '☑' : '☐',
      'EMP_NOT_SEEK': data.employmentStudyReason?.employmentStatus?.includes('not seeking employment') ? '☑' : '☐',
      'EMP_NA': data.employmentStudyReason?.employmentStatus?.includes('Not Applicable') ? '☑' : '☐',

      // Study Reason (contains check)
      'STUDY_JOB': data.employmentStudyReason?.studyReason?.includes('get a job') ? '☑' : '☐',
      'STUDY_DEV_BUS': data.employmentStudyReason?.studyReason?.includes('develop my existing business') ? '☑' : '☐',
      'STUDY_START_BUS': data.employmentStudyReason?.studyReason?.includes('start my own business') ? '☑' : '☐',
      'STUDY_DIFF_CAREER': data.employmentStudyReason?.studyReason?.includes('different career') ? '☑' : '☐',
      'STUDY_BETTER_JOB': data.employmentStudyReason?.studyReason?.includes('better job or promotion') ? '☑' : '☐',
      'STUDY_REQ_JOB': data.employmentStudyReason?.studyReason?.includes('requirement of my job') ? '☑' : '☐',
      'STUDY_EXTRA_SKILLS': data.employmentStudyReason?.studyReason?.includes('extra skills for my job') ? '☑' : '☐',
      'STUDY_ANOTHER_COURSE': data.employmentStudyReason?.studyReason?.includes('another course of study') ? '☑' : '☐',
      'STUDY_PERSONAL': data.employmentStudyReason?.studyReason?.includes('personal interest') ? '☑' : '☐',
      'STUDY_COMMUNITY': data.employmentStudyReason?.studyReason?.includes('community/voluntary work') ? '☑' : '☐',
      'STUDY_OTHER': data.employmentStudyReason?.studyReason?.includes('Other') ? '☑' : '☐',
      
      // Additional Services
      'HOMESTAY_YES': data.additionalServices?.homestay === 'Yes' ? '☑' : '☐',
      'HOMESTAY_NO': data.additionalServices?.homestay === 'No' ? '☑' : '☐',
      'AIRPORT_YES': data.additionalServices?.airportPickup === 'Yes' ? '☑' : '☐',
      'AIRPORT_NO': data.additionalServices?.airportPickup === 'No' ? '☑' : '☐',
      'ARRIVAL_DETAILS': data.additionalServices?.arrivalDetails || '',

      'CREDIT_UNITS': data.rplCredit?.creditUnits || '',
      'RPL_UNITS': data.rplCredit?.rplUnits || '',
      // USI
      'USI': data.usi?.usi || '',
      
      // Declaration
      'DECL_TRUE_YES': data.declaration?.confirmTrueAndCorrect ? '☑' : '☐',
      'DECL_TRUE_NO': !data.declaration?.confirmTrueAndCorrect ? '☑' : '☐',
      'PRIVACY_YES': data.declaration?.agreePrivacyNotice ? '☑' : '☐',
      'PRIVACY_NO': !data.declaration?.agreePrivacyNotice ? '☑' : '☐',
      'APPLICANT_NAME': data.declaration?.signatureName || '',
      'APPLICANT_SIGN': data.declaration?.signature || 'Electronically signed',
      'SIGNATURE_DATE': data.declaration?.signatureDate || '',

      
      // Checklist (short codes)
      'CHK_FORM': data.declaration?.checklist?.formCompleted ? '☑' : '☐',
      'CHK_PPT': data.declaration?.checklist?.passportAttached ? '☑' : '☐',
      'CHK_VISA': data.declaration?.checklist?.visaAttachedIfApplicable ? '☑' : '☐',
      'CHK_ENG': data.declaration?.checklist?.englishEvidenceAttached ? '☑' : '☐',
      'CHK_DEC': data.declaration?.checklist?.readSignedDeclaration ? '☑' : '☐',
      'CHK_TRNS': data.declaration?.checklist?.transcriptsAttached ? '☑' : '☐',
      'CHK_FIN': data.declaration?.checklist?.financialDocsAttached ? '☑' : '☐',
      'CHK_GS': data.declaration?.checklist?.gsFormAttached ? '☑' : '☐',

    };
  };
 const handleEnrolmentDocxForm = async () => {
    // mappingFile = the form state you want to inject

    const url = await handleDocxForm(
      "EnrolmentMapping",          // placeholder mapping data
      initialData,          // whole enrolment record
      templatePath,         // path to DOCX template
      setError,             // error handler
      true                  // requiredUpload (optional, default false)
    );
    window.open(url, "_blank");
  };
  // Generate Enrolment Form → DOCX
  const handleEnrolmentForm = async () => {
    if (!initialData?.id) return;

    try {
      // 1. Fetch template
      const response = await fetch(templatePath);
      if (!response.ok) throw new Error("Template not found");

      const arrayBuffer = await response.arrayBuffer();
      const templateMapping = createTemplateMapping(initialData.formState);

      // 2. Fill DOCX template
      const filledDoc = processDocxTemplate(arrayBuffer, templateMapping);

      const filePath = `enrolments/applicationForms/enrolment-${initialData.enrolmentReferenceId}.docx`;

      // 3. Check if file exists
      let fileUrl;
      try {
        fileUrl = await fileStorageService.getFileUrl(filePath);
      } catch (err) {
      }

      // 4. Upload (this will overwrite if file exists)
      const fileData = await fileStorageService.uploadFile(filledDoc, filePath);

      // 5. Update enrollment record
      await updateEnrollmentApplicationForm(
        initialData.id,
        initialData,
        {
          url: fileData.url,
          path: fileData.path,
          name: `enrolment-${initialData.enrolmentReferenceId}.docx`
        },
        initialData.enrolmentReferenceId
      );

      // 6. Open uploaded file in a new tab
      window.open(fileData.url, "_blank");

    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <div>
      <SectionTitle icon={CheckCircle}>Actions</SectionTitle>
      <div className="workflow-panel" style={{
        border: "1px solid #ddd",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "20px",
        background: "#f9fbfd"
      }}>
        <ol style={{ paddingLeft: "20px", margin: 0 }}>
          <li style={{ marginBottom: "20px" }}>
            <strong>Accept Enrolment:</strong> Approve the enrolment request. Enrolment status will be accepted by setting necessary details for shifting to axcelerate.
          </li>
          <li style={{ marginBottom: "20px" }}>
            <strong>Shift to Axcelerate:</strong> Once accepted, migrate the enrolment into the student system.
          </li>
          <li style={{ marginBottom: "20px" }}>
            <strong>Update Axcelerate:</strong> After migration, you can update details to Axcelerate if any changes are required.
          </li>
        </ol>
        <p style={{ marginTop: "12px", fontSize: "0.9em", color: "#555" }}>
          ⚡ Each step must be completed in order. Completed actions cannot be undone.
        </p>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={24} style={{ marginBottom: 8 }}>
          <Button
            icon={CheckCircle}
            onClick={handleAcceptEnrolment}
            disabled={!pending}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: 16,
              background: pending ? '#4dd0e1' : '#b2ebf2',
              color: pending ? '#006064' : '#757575',
              cursor: pending ? 'pointer' : 'not-allowed',
            }}
          >
            {!pending ? 'Enrolment Accepted' : 'Accept Enrolment'}
          </Button>
          <p>
            {initialData?.enrolmentAcceptedAt ? <em style={{ fontSize: 12 }}>
              Last updated at: {new Date(initialData?.enrolmentAcceptedAt).toLocaleString()}
            </em> : null}
          </p>
        </Col>

        <Col span={24} style={{ marginBottom: 8 }}>
          <Button
            icon={ArrowRightCircle}
            onClick={handleShiftToAxcelerate}
            disabled={(!pending && !accepted) || shifted}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: 16,
              background: accepted && !shifted ? '#4dd0e1' : '#b2ebf2',
              color: accepted && !shifted ? '#006064' : '#757575',
              cursor: accepted && !shifted ? 'pointer' : 'not-allowed',
            }}
          >
            {initialData?.axcelerateURL ? 'Shifted to Axcelerate' : 'Shift to Axcelerate'}
          </Button>
          <p>
            <em style={{ fontSize: 12, color: shifted ? '#0e990eff' : '#c62828' }}>
              {response.type ? response.message : ""}
            </em>
            <br />
            {initialData?.axcelerateURL ? <a href={initialData?.axcelerateURL} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
              View in Axcelerate
            </a> : null}
          </p>
        </Col>
        <Col span={24} style={{ marginBottom: 8 }}>
          <Button
            icon={ArrowRightCircle}
            onClick={handleEnrolmentForm}
            disabled={!accepted}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: 16,
              background: '#4dd0e1',
              color: '#006064',
              cursor: 'pointer',
            }}
          >
            Generate Enrolment Form
          </Button>
          <p>
            {initialData?.enrolmentApplicationForm?.url ? <a href={initialData?.enrolmentApplicationForm?.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
              View Application Form
            </a> : null}
          </p>
        </Col>


      </Row>
    </div>
  );
}
